import React, { useEffect, useMemo, useState } from "react";


function formatCreatedAt(epoch) {
  const value = Number(epoch || 0);
  if (!value) return "";
  try {
    return new Date(value * 1000).toLocaleString();
  } catch {
    return "";
  }
}

export default function ChatRoomMemoryPanel({
  open,
  roomTitle,
  memory,
  messages,
  busy,
  canClearConversation,
  onClose,
  onRebuild,
  onClearConversation,
  onDeleteSelectedMessages,
}) {
  const [selectedMessageIds, setSelectedMessageIds] = useState([]);

  const deletableMessages = useMemo(() => {
    return Array.isArray(messages) ? messages.filter((item) => String(item?.message_id || "").trim()) : [];
  }, [messages]);

  useEffect(() => {
    setSelectedMessageIds([]);
  }, [open, roomTitle, deletableMessages.length]);

  if (!open) return null;

  const roomMemory = memory?.room || null;
  const participants = Array.isArray(memory?.participants) ? memory.participants : [];

  function toggleMessage(messageId) {
    const target = String(messageId || "").trim();
    if (!target) return;
    setSelectedMessageIds((current) => {
      return current.includes(target) ? current.filter((item) => item !== target) : [...current, target];
    });
  }

  function selectAllMessages() {
    setSelectedMessageIds(deletableMessages.map((item) => String(item.message_id || "").trim()).filter(Boolean));
  }

  function clearSelection() {
    setSelectedMessageIds([]);
  }

  return (
    <div className="memory-modal-overlay" role="dialog" aria-modal="true">
      <div className="memory-modal vh-chatrooms-memory-modal">
        <div className="memory-modal-header">
          <h2>Memoria do Tribunal: {roomTitle || "Sala"}</h2>
          <button type="button" className="memory-close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="memory-content">
          <section className="vh-chatrooms-memory-section">
            <div className="vh-chatrooms-memory-title">Resumo da sala</div>
            <div className="vh-chatrooms-memory-card">
              <div className="vh-chatrooms-memory-text">
                {roomMemory?.summary_text || "A memoria da sala ainda nao foi consolidada."}
              </div>
              {Array.isArray(roomMemory?.evidence) && roomMemory.evidence.length ? (
                <div className="vh-chatrooms-memory-evidence">
                  {roomMemory.evidence.map((item, index) => (
                    <div key={`room-ev-${index}`} className="vh-chatrooms-memory-evidence-item">
                      <strong>{item.author || "Trecho"}:</strong> {item.text || ""}
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </section>

          <section className="vh-chatrooms-memory-section">
            <div className="vh-chatrooms-memory-title">Memoria por integrante</div>
            <div className="vh-chatrooms-memory-grid">
              {participants.map((item) => (
                <article
                  key={`${item.subject_user_id || item.subject_display_name}`}
                  className="vh-chatrooms-memory-card"
                >
                  <div className="vh-chatrooms-memory-person">{item.subject_display_name || "Participante"}</div>
                  <div className="vh-chatrooms-memory-text">{item.summary_text || "Sem resumo."}</div>
                  {Array.isArray(item.evidence) && item.evidence.length ? (
                    <div className="vh-chatrooms-memory-evidence">
                      {item.evidence.map((evidence, index) => (
                        <div
                          key={`${item.subject_user_id || "participant"}-${index}`}
                          className="vh-chatrooms-memory-evidence-item"
                        >
                          <strong>{evidence.author || item.subject_display_name}:</strong> {evidence.text || ""}
                        </div>
                      ))}
                    </div>
                  ) : null}
                </article>
              ))}
              {!participants.length ? (
                <div className="vh-chatrooms-memory-card">Ainda nao ha memoria individual consolidada.</div>
              ) : null}
            </div>
          </section>

          {canClearConversation ? (
            <section className="vh-chatrooms-memory-section">
              <div className="vh-chatrooms-memory-title">Apagar mensagens indicadas</div>
              {deletableMessages.length ? (
                <>
                  <div className="vh-chatrooms-memory-selection-actions">
                    <button type="button" className="btn gothic-btn" onClick={selectAllMessages} disabled={busy}>
                      Selecionar tudo
                    </button>
                    <button type="button" className="btn gothic-btn" onClick={clearSelection} disabled={busy || !selectedMessageIds.length}>
                      Limpar selecao
                    </button>
                  </div>
                  <div className="vh-chatrooms-memory-message-list">
                    {deletableMessages.map((message) => {
                      const messageId = String(message.message_id || "").trim();
                      const checked = selectedMessageIds.includes(messageId);
                      return (
                        <label
                          key={messageId}
                          className={`vh-chatrooms-memory-message-option ${checked ? "is-selected" : ""}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleMessage(messageId)}
                            disabled={busy}
                          />
                          <div className="vh-chatrooms-memory-message-body">
                            <div className="vh-chatrooms-memory-message-head">
                              <strong>{message.author_display_name_snapshot || "Participante"}</strong>
                              <span>{formatCreatedAt(message.created_at)}</span>
                            </div>
                            <div className="vh-chatrooms-memory-message-text">{message.text || ""}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div className="vh-chatrooms-memory-card">Nao ha mensagens para apagar nesta sala.</div>
              )}
            </section>
          ) : null}
        </div>

        <div className="memory-actions">
          <button type="button" className="btn gothic-btn" onClick={onRebuild} disabled={busy}>
            {busy ? "Reconstruindo..." : "Reconstruir memoria"}
          </button>
          {canClearConversation ? (
            <button
              type="button"
              className="btn gothic-btn"
              onClick={() => onDeleteSelectedMessages?.(selectedMessageIds)}
              disabled={busy || !selectedMessageIds.length}
            >
              {busy ? "Apagando..." : "Apagar selecionadas"}
            </button>
          ) : null}
          {canClearConversation ? (
            <button type="button" className="btn gothic-btn" onClick={onClearConversation} disabled={busy}>
              {busy ? "Apagando..." : "Apagar conversa"}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
